import ErrorMessage from '@shared/components/ErrorMessage/ErrorMessage';
import cx from 'classnames';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as MailIcon } from 'assets/icons/Mail.svg';
import { ReactComponent as MatrixIcon } from 'assets/icons/Matrix.svg';
import InviteChip from 'modules/CreateWorkspaceModal/WorkspaceInviteStep/WorkspaceInviteForm/InviteChip';
import InviteTextarea from 'modules/CreateWorkspaceModal/WorkspaceInviteStep/WorkspaceInviteForm/InviteTextarea';
import useInviteInputLogic from 'modules/CreateWorkspaceModal/WorkspaceInviteStep/WorkspaceInviteForm/useInviteInputLogic';
import {
  isEmailFormat,
  isMatrixIdFormat,
} from 'modules/CreateWorkspaceModal/WorkspaceInviteStep/WorkspaceInviteForm/utils';
import { getInviteeValidation } from 'utils/yup/schemas/validationSchemas';

interface InviteInputBaseProps {
  className?: string;
  invitees: string[];
  addInvitee: (invitee: string) => void;
  removeInvitee: (invitee: string) => void;
  setInvitees: (invitees: string[]) => void;
  errorMessage?: string;
  placeholder: string;
  rows: number;
  getDisplayName?: (inviteeId: string) => string | undefined;
}

function getChipIcon(invitee: string): React.ReactNode | undefined {
  if (isEmailFormat(invitee)) {
    return <MailIcon className="h-4 w-4 shrink-0 stroke-gray-80 stroke-1.5" />;
  }
  if (isMatrixIdFormat(invitee)) {
    return <MatrixIcon className="h-4 w-4 shrink-0 fill-current text-gray-80" />;
  }
  return undefined;
}

const InviteInputBase = ({
  className,
  invitees,
  addInvitee,
  removeInvitee,
  setInvitees,
  errorMessage,
  placeholder,
  rows,
  getDisplayName,
}: InviteInputBaseProps) => {
  const [invalidInvitees, setInvalidInvitees] = useState<Set<string>>(new Set());
  const { t } = useTranslation();

  const { inputValue, textareaRef, handleInputChange, handleKeyDown, handlePaste, isCurrentInputDuplicate } =
    useInviteInputLogic({
      invitees,
      addInvitee,
      removeInvitee,
      setInvitees,
    });

  const hasInvitees = invitees.length > 0;
  const hasError = !!errorMessage;

  useEffect(() => {
    const validateAllInvitees = async () => {
      const invalidSet = new Set<string>();

      for (const invitee of invitees) {
        if (isEmailFormat(invitee) || isMatrixIdFormat(invitee)) {
          try {
            const schema = getInviteeValidation();
            await schema.validate(invitee);
          } catch {
            invalidSet.add(invitee);
          }
        }
      }

      setInvalidInvitees(invalidSet);
    };

    validateAllInvitees();
  }, [invitees]);

  const handleContainerClick = () => textareaRef.current?.focus();

  return (
    <div
      className={cx(
        'px-3 py-3 border border-gray-20 hover:border-primary-50 focus-within:border-primary-50 focus-within:bg-white rounded-2xl bg-gray-10 cursor-text mb-2 flex flex-col',
        { '!border-alert-50': hasError },
        className
      )}
      onClick={handleContainerClick}
    >
      <div className="flex flex-wrap gap-2 items-start flex-1">
        {invitees.map((invitee, index) => {
          const shouldShowError = invalidInvitees.has(invitee);
          return (
            <InviteChip
              key={`${invitee}-${index}`}
              invitee={invitee}
              onRemove={removeInvitee}
              hasError={shouldShowError}
              displayLabel={getDisplayName?.(invitee)}
              icon={getChipIcon(invitee)}
            />
          );
        })}
        <InviteTextarea
          ref={textareaRef}
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          placeholder={hasInvitees ? '' : placeholder}
          hasInvitees={hasInvitees}
          rows={rows}
        />
      </div>
      {(hasError || isCurrentInputDuplicate) && (
        <div className="mt-5">
          <ErrorMessage className="!m-0" message={hasError ? errorMessage : t('workspaces.duplicate_email_error')} />
        </div>
      )}
    </div>
  );
};

export default InviteInputBase;
